export interface BookingRow {
  id: string
  user_id: string
  credit_delta: number
  reason: string
  calcom_booking_uid: string | null
  metadata: {
    start_time?: string
    end_time?: string
    event_type_slug?: string
    title?: string
  }
  created_at: string
}

export interface BookingFilters {
  startDate?: string   // ISO 8601 UTC
  endDate?: string     // ISO 8601 UTC
  reason?: string
  userId?: string
}

export interface AuthUser {
  id: string
  email: string
  /**
   * `auth.users.raw_user_meta_data->>'full_name'` — set when a user provides
   * a name during signup, otherwise null. Surfaced by `get_all_users()` since
   * the Phase 61 UAT 2026-06-23 migration so the admin /reservations row title
   * can show the person name instead of falling back to the email or user_id.
   * Optional so existing test fixtures (pre-migration shape) keep compiling ;
   * the runtime data always includes it now, possibly null.
   */
  full_name?: string | null
  /**
   * `public.profiles.phone` (E.164) — LEFT JOINed by `get_all_users()` since
   * the 2026-07-30 migration so the admin fiche utilisateur can show the
   * client's number. NULL when the user has no profile row or no phone.
   * Optional so pre-migration test fixtures keep compiling.
   */
  phone?: string | null
  /**
   * Derived by `get_all_users()` since the Phase 94 migration
   * (20260812120000): true ONLY when BOTH name homes are empty
   * (`auth.users.raw_user_meta_data->>'full_name'` and `public.profiles.full_name`)
   * AND `public.profiles.name_lookup_failed_at` is stamped — i.e. every source,
   * Stripe included, was exhausted and no name exists.
   * `false` means **not processed yet**, NOT "name known" — the two are different
   * states and the admin list must not render them alike. A name arriving later
   * flips the marker back to false on its own (the RPC derives it, never stores it).
   * Optional so pre-migration test fixtures keep compiling ; the runtime data
   * always includes it now.
   */
  name_unknown?: boolean
  created_at: string
  banned_until: string | null
}

export interface CreditBalance {
  user_id: string
  balance: number
  expires_at: string | null
}

export interface UserWithBalance extends AuthUser {
  balance: number
  expires_at: string | null
}

export function applyBookingFilters(rows: BookingRow[], filters: BookingFilters): BookingRow[] {
  return rows.filter((row) => {
    const startTime = row.metadata.start_time ?? row.created_at
    if (filters.startDate && startTime < filters.startDate) return false
    if (filters.endDate && startTime > filters.endDate) return false
    if (filters.reason && row.reason !== filters.reason) return false
    if (filters.userId && row.user_id !== filters.userId) return false
    return true
  })
}

export function joinUsersWithBalance(users: AuthUser[], balances: CreditBalance[]): UserWithBalance[] {
  const balanceMap = new Map(balances.map((b) => [b.user_id, b]))
  return users.map((u) => ({
    ...u,
    balance: balanceMap.get(u.id)?.balance ?? 0,
    expires_at: balanceMap.get(u.id)?.expires_at ?? null,
  }))
}

// === Phase 21 v1.3 Bloc B — Custom booking schema ===
// Cohabite avec BookingRow heritage v1.1 (Cal.com-derived bookings_ledger).
// Phase 22 dual-stack canary lit les 2 sources ; Phase 24 cutover supprime BookingRow.

export interface CustomBookingRow {
  id: string
  user_id: string
  status: 'held' | 'confirmed' | 'cancelled' | 'completed' | 'no_show'
  event_type_slug: 'coaching-60min'
  slot_starts_at: string  // ISO 8601 timestamptz
  slot_ends_at: string
  held_until: string | null
  confirmed_at: string | null
  cancelled_at: string | null
  // P0-1 iter 2: DB is varchar(200) free text (no CHECK enum). UI dropdown
  // options come from cancellationReasons.ts (5 categorical) but DB accepts
  // any string. Audit trail catégoriel vit dans webhook_events.payload.
  cancellation_reason: string | null
  reschedule_count: number
  last_rescheduled_at: string | null
  pack_purchase_id: string | null
  created_at: string
  // Phase 27+ — native SwitchBot wiring. Populated when the Stripe webhook
  // calls createTimeLimitedPin on the keypad. Useful for admin to map a
  // code visible in the SwitchBot mobile app back to the booking row.
  pin_code: string | null
  switchbot_key_name: string | null
  switchbot_status: 'ok' | 'skipped_vault_empty' | 'error' | null
  // Phase 40 D-05a — native SwitchBot failure-detail columns. All four already
  // exist on public.bookings (migrations 20260513220000:17-19 + 20260520200000:44-47);
  // populated by record_booking_switchbot_outcome. Surfaced in the admin list/calendar.
  switchbot_error: string | null              // failure reason text
  switchbot_attempted_at: string | null        // last attempt timestamptz (ISO)
  switchbot_retry_count: number                 // int, NOT NULL DEFAULT 0, caps at 5
  switchbot_history: Array<{ at: string; status: string; error: string | null; key_name: string | null }>  // jsonb, parsed by PostgREST
  // Soft-hide cosmetic for /admin/reservations (migration 20260527132340).
  // Set by admin_bulk_hide_bookings, cleared by admin_bulk_unhide_bookings.
  // NULL = visible. Pure UX flag — no ledger / slot / cascade impact.
  admin_hidden_at: string | null
  admin_hidden_by: string | null
}

export interface CustomBookingFilters {
  startDate?: string  // ISO 8601 — filter slot_starts_at >= startDate
  endDate?: string    // ISO 8601 — filter slot_starts_at <= endDate
  status?: CustomBookingRow['status']
  userId?: string
}

export function applyCustomBookingFilters(
  rows: CustomBookingRow[],
  filters: CustomBookingFilters,
): CustomBookingRow[] {
  return rows.filter((row) => {
    if (filters.startDate && row.slot_starts_at < filters.startDate) return false
    if (filters.endDate && row.slot_starts_at > filters.endDate) return false
    if (filters.status && row.status !== filters.status) return false
    if (filters.userId && row.user_id !== filters.userId) return false
    return true
  })
}

// === Phase 40 D-04 — client-side sort comparator ===
// allBookings is fully in memory; the toggle re-sorts a COPY in a useMemo (never
// mutate React state). Date.parse (not lexical compare) so DST/offset is safe.
export type BookingSortMode = 'slot_desc' | 'slot_asc' | 'created_desc' | 'created_asc'

export function sortBookings(rows: CustomBookingRow[], mode: BookingSortMode): CustomBookingRow[] {
  const key = mode.startsWith('slot') ? 'slot_starts_at' : 'created_at'
  const dir = mode.endsWith('asc') ? 1 : -1
  return [...rows].sort((a, b) => ((Date.parse(a[key] ?? '') || 0) - (Date.parse(b[key] ?? '') || 0)) * dir)
}

// === Phase 40 D-05d / D-06 helper — shared eligibility/problem predicates ===
// Single source of each rule so the list and the calendar never diverge. All
// three take an injectable `now` so tests pin time deterministically.

// Expired pre-reservation: a held row whose held_until has passed (the cron
// should have released it). Centralized so the filter and the Plan 03 badge agree.
// A held row with held_until IS NULL is malformed (reserve_slot always stamps
// now()+15min) — treat it as expired so it can't zombie as a permanent active hold.
export function isHeldExpired(row: CustomBookingRow, now = Date.now()): boolean {
  return row.status === 'held' && (!row.held_until || Date.parse(row.held_until) < now)
}

// A "problem" booking = the PIN failed to program OR an expired held. Composes
// isHeldExpired (no re-inlined held-expired math) so the rule has one definition.
export function isProblemBooking(row: CustomBookingRow, now = Date.now()): boolean {
  return row.switchbot_status === 'error' || isHeldExpired(row, now)
}

// Cancel-eligibility: held|confirmed AND a parseable future slot. The single
// source the list (AdminReservations) and the calendar panel (Plan 04) share —
// it reproduces the existing inline predicate exactly (Gemini Gate 1 #2).
export function isBookingCancellable(row: CustomBookingRow, now = Date.now()): boolean {
  return (
    (row.status === 'held' || row.status === 'confirmed') &&
    typeof row.slot_starts_at === 'string' &&
    !Number.isNaN(Date.parse(row.slot_starts_at)) &&
    Date.parse(row.slot_starts_at) > now
  )
}
